import { UIElement } from '../ui-element';
import { setInnerText } from '../../global/ui-helpers';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import styles from './ui-hint.css';

/**
 * @typedef {object} UIHintType
 * @property {Extract<UIHintType, 'error'>} Error - Error type.
 * @property {Extract<UIHintType, 'info'>} Info - Info type.
 */

enum UIHintType {
    ERROR = 'error',
    INFO = 'info',
}

/**
 * @class
 * @memberof SharedComponents
 * @element ui-hint
 * @augments {UIElement}
 * @alias UIHint
 * @element ui-hint
 * @classdesc Represents a class for <code>ui-hint</code> element.
 * Hint is usually used inside ui-field to provide a help hint for field.
 * @property {UIHintType} type {@attr type} Type of the hint.
 *  {@desc error}
 *  {@desc info}
 * @property {string} errorType {@attr error-type} Error for validity type, usually you never
 * should add this manually.
 * @slot
 * @example
 * <ui-hint>Hello world I am hint.</ui-hint>
 * @example
 * <ui-hint type="error">Hint error</ui-hint>
 */
@Component({
    tag: 'ui-hint',
    styles,
})
export class UIHint extends UIElement {
    @Attribute() type!: UIHintType;
    @Attribute() errorType!: string;

    /**
     * @type {UIHintType}
     */
    static get types() {
        return UIHintType;
    }

    /**
     * Sets message content for hint.
     * @param {string} message
     * @returns {UIHint}
     */
    setMessage(message: string): this {
        setInnerText(this, message);
        return this;
    }

    /**
     * Ensures the error icon is present for error hints
     * @private
     */
    private ensureErrorIcon(): void {
        if (this.type === UIHint.types.ERROR) {
            if (
                !this.querySelector(
                    'ui-icon[glyph="redesign-empty-circle-exclamation"]'
                )
            ) {
                const icon = this.createElement({
                    tagName: 'ui-icon',
                    attributes: {
                        glyph: 'redesign-empty-circle-exclamation',
                    },
                });
                this.insertBefore(icon, this.firstChild);
            }
        }
    }

    /**
     * @inheritDoc
     */
    render(): void {
        this.ensureErrorIcon();
    }
}
